import {PolymerElement, html} from '@polymer/polymer';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {LeafletSharedStylesHTML} from './shared-styles/leaflet.js';
import {ResizeObserverBehavior} from './behaviors/resize-observer-behavior.js';
import {renderKeliMarkdown} from '../javascripts/modules/renderKeliMarkdown.js';

class KeliMap extends ScopeBinding(ResizeObserverBehavior(PolymerElement)) {
  static get template() {
    return html`
${LeafletSharedStylesHTML}
<style>
  :host {
    display: block;
    min-height: 100px;
    height: var(--keli-map-height, 100px);
  }
  
  #map {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }
</style>

<div id="map"></div>`;
  }

  static get is() {
    return 'keli-map';
  }

  static get properties() {
    return {
      height: {type: Number, scopevar: true, observer: 'heightChanged'},
      keliControlTypes: {type: Array, value: ['map']},
      location: {type: String, scopevar: true, observer: 'locationChanged'},
      mapInstance: Object,
      mapObjects: {type: Array, computed: 'computeMapObjects(markers, circles)'},
      markers: {type: Array, scopevar: true},
      circles: {type: Array, scopevar: true},
      zoom: {type: Number, scopevar: true, observer: 'zoomChanged'},
    }
  }

  connectedCallback() {
    super.connectedCallback();

    if (window.L) {
      const mapBoxURL = 'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}';
      const mapBoxDefaults = {
        attribution: '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors | © <a href="https://www.mapbox.com/">Mapbox</a>',
        accessToken: 'pk.eyJ1Ijoia2VsbHBybyIsImEiOiJjanR0MmllZGQwejFnNGVwZXIwbGg0bW1tIn0.GcKE3aZdBzVNudi39v5caQ',
        maxZoom: 20
      };

      window.L.Icon.Default.imagePath = '/images/leaflet/';
      this.mapInstance = window.L.map(this.$.map, {
        scrollWheelZoom: false
      }).fitWorld();
      let baseLayers = {
        'Streets': window.L.tileLayer(mapBoxURL, Object.assign({}, mapBoxDefaults, {id: 'mapbox/streets-v9'})),
        'Satellite': window.L.tileLayer(mapBoxURL, Object.assign({}, mapBoxDefaults, {id: 'mapbox/satellite-streets-v9'}))
      };
      baseLayers.Streets.addTo(this.mapInstance);
      window.L.control.layers(baseLayers).addTo(this.mapInstance);
      this.mapInstance.on('focus', () => this.mapInstance.scrollWheelZoom.enable());
      this.mapInstance.on('blur', () => this.mapInstance.scrollWheelZoom.disable());
    }

    afterNextRender(this, () => {
      this.mapInstance && this.mapInstance.invalidateSize();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.mapInstance) {
      this.mapInstance.remove();
      this.mapInstance = undefined;
    }
  }

  handleElementResized() {
    this._handleElementResizedJob = Debouncer.debounce(this._handleElementResizedJob, timeOut.after(10), () => {
      afterNextRender(this, () => {
        this.mapInstance && this.mapInstance.invalidateSize();
      });
    });
  }

  heightChanged(newValue) {
    let height = '';
    if (newValue) {
      height = `${parseInt(newValue, 10)}px`;
    }
    this.updateStyles({'--keli-map-height': height});
  }

  computeMapObjects(markers, circles) {
    if (!this.mapInstance) {
      return;
    }

    if (Array.isArray(this.mapObjects)) {
      this.mapObjects.forEach(mark => {
        mark.removeFrom(this.mapInstance);
      });
    }

    const newMapObjects = [];
    if (Array.isArray(markers)) {
      markers.forEach(mark => {
        let location = this.parseLocation(mark.location || mark);
        if (location) {
          let newMarker = new window.L.marker(location);
          if (mark.caption) {
            newMarker.bindTooltip(mark.caption).openTooltip();
          }
          if (mark.description) {
            newMarker.bindPopup(renderKeliMarkdown(mark.description)).openPopup();
          }
          newMapObjects.push(newMarker);
          newMarker.addTo(this.mapInstance);
        }
      });
    }

    if (Array.isArray(circles)) {
      circles.forEach(mark => {
        let location = this.parseLocation(mark.location || mark);
        if (location) {
          let newCircle = new window.L.circle(location, (mark.radius ? parseInt(mark.radius, 10) : 20));
          newMapObjects.push(newCircle);
          newCircle.addTo(this.mapInstance);
        }
      });
    }

    if (!this.zoom && newMapObjects.length) {
      afterNextRender(this, () => {
        this.mapInstance.invalidateSize();
        let group = window.L.featureGroup(newMapObjects);
        this.mapInstance.fitBounds(group.getBounds().pad(0.2));
      });
    }
    return newMapObjects;
  }

  zoomChanged(newValue) {
    if (!this.mapInstance) {
      return;
    }
    let zoom = parseInt(newValue, 10);
    if (zoom) {
      afterNextRender(this, () => {
        this.mapInstance.invalidateSize();
        this.mapInstance.setZoom(zoom);
      });
    }
  }

  locationChanged(newValue) {
    if (!this.mapInstance) {
      return;
    }
    let location = this.parseLocation(newValue);
    if (location) {
      this.mapInstance.setView(location, this.mapInstance.getZoom());
    }
  }

  parseLocation(value) {
    if (!value || typeof value !== 'string') {
      return undefined;
    }
    let parts = value.split(',');
    return window.L.latLng(parseFloat(parts[0]), parseFloat(parts[1]));
  }
}

customElements.define(KeliMap.is, KeliMap);
export {KeliMap};
